<template>
    <div>
        <div class="horizontal">
            <div :id="'scichart-root-herd-weight-'+sid" style="width: 100%; margin-right: 10px;"></div>
        </div>
    </div>
</template>

<script>
import { getHerdWeight } from '@/api/api';
import moment from 'moment'

import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { EllipsePointMarker } from "scichart/Charting/Visuals/PointMarkers/EllipsePointMarker";
import { SciChartJSLightTheme } from "scichart/Charting/Themes/SciChartJSLightTheme";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"
import { NumberRange } from 'scichart/Core/NumberRange';
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import { XyLinearTrendFilter } from 'scichart/Charting/Model/Filters/XyLinearTrendFilter';

export default {
    props: {
        systemId: {
            required: true,
        },
        sid: {
            default: 0,
            required: false,
        }
    },
    data() {
        return {
            sciChartSurface: null,
            wasmContext: null,
            xAxis: null,
            yAxis: null
        }
    },
    methods: {
        async getData() {
            getHerdWeight(this.systemId).then((result) => {
                this.initSciChart(result);
            });
        },
        async initSciChart(data){
            var xValues = data.labels
            var yValues = data.values

            const {sciChartSurface, wasmContext} = await SciChartSurface.create('scichart-root-herd-weight-'+this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            await sciChartSurface.applyTheme(new SciChartJSLightTheme())

            this.xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            this.yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});

            this.yAxis.growBy = new NumberRange(0.1, 0.1);
            this.xAxis.growBy = new NumberRange(0.01, 0.01);

            sciChartSurface.xAxes.add(this.xAxis)
            sciChartSurface.yAxes.add(this.yAxis)

            this.xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("YYYY-MM-DD")
            };

            var d = [];

            const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                stroke: "#0095B2",
                drawNaNAs: ELineDrawMode.DiscontinuousLine,
                strokeThickness: 2,
                pointMarker: new EllipsePointMarker(wasmContext, {
                    width: 6,
                    height: 6,
                    strokeThickness: 2,
                    fill: "#0095B230",
                    stroke: "#0095B2"
                })
            });

            if(!(xValues == undefined || xValues == null || xValues.length == 0)) {
                for(let i = 0; i < xValues.length; i++){
                    d[i] = new Date(moment(xValues[i])).getTime()
                }

                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: yValues.map((x) => Math.round(x / 1000, 2))
                });

                lineSeries.strokeThickness = 3;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
                lineSeries.rolloverModifierProps.tooltipTemplate = this.tooltipTemplateMethod;
            } else {
                d = {}

                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: {},
                    yValues: {}
                });
            }

            this.sciChartSurface.renderableSeries.add(lineSeries);

            // TREND LINE 
            const linearTrendFilter = new XyLinearTrendFilter(
                new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: yValues.map((x) => Math.round(x / 1000, 2))
                }), 
                { dataSeriesName: this.$t('trendLine') }
            );
            const filteredLine = new FastLineRenderableSeries(this.wasmContext, { 
                dataSeries: linearTrendFilter, 
                stroke: "#ff0000",
                strokeThickness: 2,
                rolloverModifier: null
            });
            filteredLine.rolloverModifierProps.tooltipTemplate = this.trendTemplateMethod;

            this.sciChartSurface.renderableSeries.add(filteredLine)
            // END OF TREND LINE

            const rolloverModifier = new RolloverModifier();
            rolloverModifier.showRolloverLine = false;
            this.sciChartSurface.chartModifiers.add(rolloverModifier);

            // this.sciChartSurface.chartModifiers.add(new CursorModifier({
            //     crosshairStroke: "#0095B2",
            //     crosshairStrokeThickness: 1,
            //     tooltipContainerBackground: "#000",
            //     tooltipTextStroke: "#0095B2",
            //     showTooltip: false,
            //     axisLabelFill: "#0095B2",
            //     axisLabelStroke: "#fff",
            // }))
        },
        tooltipTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const { _, tooltipColor, tooltipTextColor } = rolloverTooltip.tooltipProps;
            const width = 200;
            const height = 120;
            rolloverTooltip.updateSize(width, height); 

            return `<svg width="${width}" height="${height}">
                <rect x="10" height="40" width="150" rx="5" style="stroke-width:1; stroke:rgb(0,0,0)" fill="#fff"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${tooltipTextColor}">
                        <tspan x="20" dy="1.2em">${moment(Math.floor(seriesInfo.formattedXValue)).format("DD-MM-YYYY")}</tspan>
                        <tspan x="20" dy="1.2em">${parseFloat(seriesInfo.formattedYValue).toFixed(2)} kg</tspan>
                    </text>
                    <defs>
                        <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                        </filter>
                    </defs>
                </svg>
                </svg>`;
        },
        trendTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const { tooltipTitle, tooltipColor, tooltipTextColor } = rolloverTooltip.tooltipProps;
            const width = 200;
            const height = 50;
            rolloverTooltip.updateSize(width, height);

            return `<svg width="${width}" height="${height}">
                <rect x="10" height="30" width="150" rx="5" style="stroke-width:2; stroke:${tooltipColor}" fill="#fff"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${tooltipTextColor}">
                        <tspan x="20" dy="1.2em">${seriesInfo.formattedYValue} kg</tspan>
                    </text>
                    <defs>
                    <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                    </filter>
                    </defs>
                </svg>
            </svg>`;
        },
    },
    mounted: async function() {
        this.getData();
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    }
}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/Charts/SciChartHerdWeight.vue